import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { fmtCurrency } from "@/lib/currency";
import { toast } from "sonner";
import { CalendarBlank, DownloadSimple, Receipt } from "@phosphor-icons/react";

const fmt = (n) => fmtCurrency(n || 0, "PHP");

function KpiTile({ label, value, tone = "default" }) {
  const toneCls = {
    default: "bg-card",
    dark: "bg-foreground text-background",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
  }[tone] || "bg-card";
  return (
    <div className={`border border-border p-5 ${toneCls}`}>
      <div className={`text-[10px] uppercase tracking-widest font-medium mb-3 ${tone === "dark" ? "text-background/60" : "opacity-70"}`}>{label}</div>
      <div className="font-heading font-black tabular-nums text-2xl sm:text-3xl leading-none tracking-tight">{value}</div>
    </div>
  );
}

const SIDES = [
  {
    key: "sales",
    label: "Sales (Output VAT)",
    caption: "BIR SLSP · Cash Basis · Sales",
    heading: "VAT Collections Summary",
    subheading: "Payments received in the selected month, grouped by client. Zero-rated and exempt sales are reported separately.",
    endpoint: "/reports/vat-summary",
    xlsxUrl: (m) => `/api/reports/vat-summary/xlsx?month=${encodeURIComponent(m)}`,
    fileName: (m) => `vat_summary_${m}.xlsx`,
    kpiTotal: "Total Collected", kpiVat: "Output VAT", kpiNet: "Net Revenue",
    groupName: "Client", groupKey: "clients", groupIdField: "client_id", groupNameField: "client_name",
    fieldTotal: "total_collected", fieldVat: "vat_collected", fieldNet: "net_collected",
    fieldsBreakdown: ["vatable_net", "zero_rated_net", "exempt_net"],
    detailKey: "invoices", detailNo: "invoice_no", detailPartyField: "client_name",
    detailDateField: "date", detailAmount: "amount", detailVat: "vat_portion", detailNet: "net_amount",
    detailExtra: "method", detailExtraLabel: "Method",
    perGroupCountField: "invoice_count", perGroupExtraLabel: "Invoices", perGroupExtraLabel2: "Payments", perGroupExtraField2: "payment_count",
    totalsCount: "payment_count", totalsSecondCount: "client_count", totalsCountLabel: "Payments", totalsSecondCountLabel: "Clients",
  },
  {
    key: "purchases",
    label: "Purchases (Input VAT)",
    caption: "BIR SLSP · Cash Basis · Purchases",
    heading: "Input VAT Summary",
    subheading: "Supplier bills paid in the selected month, grouped by supplier. Zero-rated and exempt purchases are reported separately.",
    endpoint: "/reports/purchase-vat-summary",
    xlsxUrl: (m) => `/api/reports/purchase-vat-summary/xlsx?month=${encodeURIComponent(m)}`,
    fileName: (m) => `purchase_vat_summary_${m}.xlsx`,
    kpiTotal: "Total Paid", kpiVat: "Input VAT", kpiNet: "Net Purchases",
    groupName: "Supplier", groupKey: "suppliers", groupIdField: "supplier_id", groupNameField: "supplier_name",
    fieldTotal: "total_paid", fieldVat: "vat_paid", fieldNet: "net_paid",
    fieldsBreakdown: ["vatable_net", "zero_rated_net", "exempt_net"],
    detailKey: "billings", detailNo: "billing_no", detailPartyField: "supplier_name",
    detailDateField: "paid_at", detailAmount: "amount", detailVat: "vat_amount", detailNet: "net_amount",
    detailExtra: "invoice_number", detailExtraLabel: "Supplier Invoice",
    perGroupCountField: "billing_count", perGroupExtraLabel: "Bills", perGroupExtraLabel2: null, perGroupExtraField2: null,
    totalsCount: "billing_count", totalsSecondCount: "supplier_count", totalsCountLabel: "Bills Paid", totalsSecondCountLabel: "Suppliers",
  },
];

export default function VatSummaryPage() {
  const [side, setSide] = useState("sales");
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const spec = SIDES.find((s) => s.key === side) || SIDES[0];

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(spec.endpoint, { params: { month } });
      setData(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load VAT summary");
      setData(null);
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, side]);

  async function downloadXlsx() {
    try {
      const token = localStorage.getItem("access_token");
      const url = `${process.env.REACT_APP_BACKEND_URL}${spec.xlsxUrl(month)}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("download failed");
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = spec.fileName(month);
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      toast.success(`${spec.label} for ${month} downloaded`);
    } catch { toast.error("Excel download failed"); }
  }

  const totals = data?.totals || {};
  const groups = data?.[spec.groupKey] || [];
  const details = data?.[spec.detailKey] || [];

  return (
    <div className="space-y-6" data-testid="vat-summary-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2 flex items-center gap-1.5"><Receipt size={12} /> {spec.caption}</div>
          <h1 className="section-title text-3xl sm:text-4xl">{spec.heading}</h1>
          <p className="text-sm text-muted-foreground mt-1">{spec.subheading}</p>
        </div>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex items-center gap-2">
            <CalendarBlank size={16} className="text-muted-foreground" />
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="border-border w-[170px]" data-testid="vat-month-input" />
          </div>
          <Button onClick={downloadXlsx} data-testid="vat-download-xlsx">
            <DownloadSimple size={16} className="mr-2" /> Download Excel
          </Button>
        </div>
      </div>

      {/* Sales / Purchases tabs */}
      <div className="flex gap-1 border border-border rounded-md p-1 bg-card w-fit" data-testid="vat-side-tabs">
        {SIDES.map((s) => (
          <button
            key={s.key}
            onClick={() => setSide(s.key)}
            className={`px-4 py-1.5 text-sm rounded transition ${side === s.key ? "bg-foreground text-background font-medium" : "hover:bg-muted"}`}
            data-testid={`vat-tab-${s.key}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiTile label={spec.kpiTotal} value={fmt(totals[spec.fieldTotal])} tone="dark" />
        <KpiTile label={spec.kpiVat} value={fmt(totals[spec.fieldVat])} tone="amber" />
        <KpiTile label={spec.kpiNet} value={fmt(totals[spec.fieldNet])} tone="emerald" />
        <KpiTile label={`${spec.totalsCountLabel} · ${spec.totalsSecondCountLabel}`} value={`${totals[spec.totalsCount] || 0} · ${totals[spec.totalsSecondCount] || 0}`} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <KpiTile label="Vatable Net" value={fmt(totals.vatable_net)} />
        <KpiTile label="Zero-Rated Net" value={fmt(totals.zero_rated_net)} />
        <KpiTile label="VAT-Exempt Net" value={fmt(totals.exempt_net)} />
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <div className="font-heading font-bold tracking-tight">Per-{spec.groupName} Summary (SLSP)</div>
          <div className="text-xs text-muted-foreground tabular-nums">{groups.length} {spec.groupName.toLowerCase()}{groups.length === 1 ? "" : "s"}</div>
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{spec.groupName}</TableHead>
              <TableHead>TIN</TableHead>
              <TableHead className="text-right">{spec.kpiTotal}</TableHead>
              <TableHead className="text-right">{spec.kpiVat}</TableHead>
              <TableHead className="text-right">{spec.kpiNet}</TableHead>
              <TableHead className="text-right">Vatable</TableHead>
              <TableHead className="text-right">Zero-Rated</TableHead>
              <TableHead className="text-right">Exempt</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
            ) : groups.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="py-8 text-center text-sm text-muted-foreground">No {side === "sales" ? "collections" : "supplier payments"} for {month}.</TableCell></TableRow>
            ) : groups.map((g, i) => (
              <TableRow key={g[spec.groupIdField] || i} data-testid={`vat-${spec.groupName.toLowerCase()}-row-${g[spec.groupIdField] || i}`}>
                <TableCell className="font-medium">{g[spec.groupNameField]}</TableCell>
                <TableCell className="font-mono text-xs">{g.tax_id || "—"}</TableCell>
                <TableCell className="text-right tabular-nums font-heading font-bold">{fmt(g[spec.fieldTotal])}</TableCell>
                <TableCell className="text-right tabular-nums text-amber-700">{fmt(g[spec.fieldVat])}</TableCell>
                <TableCell className="text-right tabular-nums text-emerald-700">{fmt(g[spec.fieldNet])}</TableCell>
                <TableCell className="text-right tabular-nums">{fmt(g.vatable_net)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmt(g.zero_rated_net)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmt(g.exempt_net)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {details.length > 0 && (
        <div className="border border-border rounded-md bg-card overflow-x-auto">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div className="font-heading font-bold tracking-tight">{side === "sales" ? "Payments Detail" : "Bills Detail"}</div>
            <div className="text-xs text-muted-foreground tabular-nums">{details.length} {side === "sales" ? "payment" : "bill"}{details.length === 1 ? "" : "s"}</div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>{side === "sales" ? "Invoice" : "Billing No."}</TableHead>
                <TableHead>{spec.groupName}</TableHead>
                <TableHead>VAT Status</TableHead>
                <TableHead className="text-right">{spec.kpiTotal}</TableHead>
                <TableHead className="text-right">{spec.kpiVat}</TableHead>
                <TableHead className="text-right">Net</TableHead>
                <TableHead>{spec.detailExtraLabel}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {details.map((d, i) => (
                <TableRow key={`${d[spec.detailNo]}-${i}`} data-testid={`vat-detail-row-${i}`}>
                  <TableCell className="text-xs text-muted-foreground">{(d[spec.detailDateField] || "").slice(0, 10)}</TableCell>
                  <TableCell className="font-mono text-xs">{d[spec.detailNo]}</TableCell>
                  <TableCell className="text-sm">{d[spec.detailPartyField]}</TableCell>
                  <TableCell>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${
                      d.vat_status === "vatable" ? "border-amber-300 bg-amber-50 text-amber-900" :
                      d.vat_status === "zero_rated" ? "border-blue-300 bg-blue-50 text-blue-900" :
                      "border-zinc-300 bg-zinc-50 text-zinc-700"
                    }`}>{d.vat_status_label}</span>
                  </TableCell>
                  <TableCell className="text-right tabular-nums font-heading font-bold">{fmt(d[spec.detailAmount])}</TableCell>
                  <TableCell className="text-right tabular-nums text-amber-700">{fmt(d[spec.detailVat])}</TableCell>
                  <TableCell className="text-right tabular-nums text-emerald-700">{fmt(d[spec.detailNet])}</TableCell>
                  <TableCell className="text-sm">{d[spec.detailExtra] || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}
